import { useMemo } from 'react';

/**
 * Commitment-specific filter hook with 8 dimensions.
 * AND between filter types, OR within each type.
 *
 * @param {Array} commitments - Commitment data array
 * @param {Object} filters - Filter state object
 * @param {string[]} filters.gyms - Gym names
 * @param {string[]} filters.tls - TL names
 * @param {string[]} filters.managers - Manager names
 * @param {string[]} filters.statuses - commitment_status values (pending, partial, fulfilled, not_interested)
 * @param {string[]} filters.verificationStatuses - (tl_only, mgr_only, both, unverified)
 * @param {{ from: string|null, to: string|null }} filters.dateRange
 * @param {{ min: number|null, max: number|null }} filters.amountRange
 * @param {number[]} filters.moveCounts - movement count values (0, 1, 2, 3)
 * @returns {Array} Filtered commitments
 */
export function useCommitmentFilter(commitments, filters) {
  return useMemo(() => {
    if (!commitments || commitments.length === 0) {
      return commitments || [];
    }

    // Quick check: if no filters active, return all
    const hasActiveFilters =
      (filters.gyms?.length > 0) ||
      (filters.tls?.length > 0) ||
      (filters.managers?.length > 0) ||
      (filters.statuses?.length > 0) ||
      (filters.verificationStatuses?.length > 0) ||
      (filters.dateRange?.from || filters.dateRange?.to) ||
      (filters.amountRange?.min !== null && filters.amountRange?.min !== undefined) ||
      (filters.amountRange?.max !== null && filters.amountRange?.max !== undefined) ||
      (filters.moveCounts?.length > 0);

    if (!hasActiveFilters) {
      return commitments;
    }

    return commitments.filter((c) => {
      // Gym filter
      const gymMatch = !filters.gyms?.length || filters.gyms.includes(c.gym_name);

      // TL filter
      const tlMatch = !filters.tls?.length || filters.tls.includes(c.tl_name);

      // Manager filter
      const managerMatch = !filters.managers?.length || filters.managers.includes(c.manager_name);

      // Status filter
      const statusMatch = !filters.statuses?.length || filters.statuses.includes(c.commitment_status);

      // Verification status filter - map booleans to string state
      let verificationMatch = true;
      if (filters.verificationStatuses?.length > 0) {
        const tlVerified = !!c.commitment_tl_verified;
        const mgrVerified = !!c.commitment_mgr_verified;
        let verificationState;
        if (tlVerified && mgrVerified) verificationState = 'both';
        else if (tlVerified && !mgrVerified) verificationState = 'tl_only';
        else if (!tlVerified && mgrVerified) verificationState = 'mgr_only';
        else verificationState = 'unverified';
        verificationMatch = filters.verificationStatuses.includes(verificationState);
      }

      // Date range filter
      let dateMatch = true;
      if (filters.dateRange?.from || filters.dateRange?.to) {
        const entryDate = c.current_date || c.entry_date || '';
        if (filters.dateRange.from && entryDate < filters.dateRange.from) dateMatch = false;
        if (filters.dateRange.to && entryDate > filters.dateRange.to) dateMatch = false;
      }

      // Amount range filter
      let amountMatch = true;
      const minVal = filters.amountRange?.min;
      const maxVal = filters.amountRange?.max;
      if (minVal !== null && minVal !== undefined && minVal !== '') {
        if ((c.revenue_expected || 0) < Number(minVal)) amountMatch = false;
      }
      if (maxVal !== null && maxVal !== undefined && maxVal !== '') {
        if ((c.revenue_expected || 0) > Number(maxVal)) amountMatch = false;
      }

      // Movement count filter
      const moveMatch = !filters.moveCounts?.length ||
        filters.moveCounts.includes((c.movements || []).length);

      return gymMatch && tlMatch && managerMatch && statusMatch &&
        verificationMatch && dateMatch && amountMatch && moveMatch;
    });
  }, [commitments, filters]);
}

/**
 * Extract unique filter options from commitment data.
 */
export function extractFilterOptions(commitments) {
  if (!commitments || commitments.length === 0) {
    return { gyms: [], tls: [], managers: [] };
  }
  return {
    gyms: [...new Set(commitments.map(c => c.gym_name).filter(Boolean))].sort(),
    tls: [...new Set(commitments.map(c => c.tl_name).filter(Boolean))].sort(),
    managers: [...new Set(commitments.map(c => c.manager_name).filter(Boolean))].sort()
  };
}
